import { Box, Container, Grid, styled } from "@mui/material";
import { HELP_QUESTIONS_TRADE, convertTabValue } from "./components/constants";
import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  getMarketLoggedInForm,
  getSingleMarketLoggedInList,
} from "store/slices/market/marketSlice";
import {
  getTradeAmountMax,
  getTradeInitial,
} from "store/slices/trade/tradeSlice";
import { useEffect, useState } from "react";

import BasicTradeFormWrapper from "./BasicTradeFormWrapper";
import MarketDrawer from "./components/MarketDrawer";
import Modal from "components/Modal";
import TradeFormSkeleton from "components/Skeletons/TradeForm.skeleton";
import TradeHead from "./components/TradeHead";
import TradeHistory from "./components/TradeHistory";
import TradingViewChart from "./components/TradeChartView/Components/TradingViewChart";
import { repeat } from "utils/helpers/interval";
import useDispatch from "hooks/useDispatch";
import useIsMobileView from "hooks/useIsMobileView";
import { useSelector } from "react-redux";
import { ROUTE_PATHS } from "routes/paths";

let getTradeAmountMaxInterval;
let getSingleMarketLoggedInListInterval;

export function BasicTrade() {
  const isMobileView = useIsMobileView();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { source, destination } = useParams();

  const convertTextToHTML = (text) => {
    return { __html: text };
  };

  const tradeInitial = useSelector((state) => state.trade.tradeInitial?.data);
  const tradeAmountMax = useSelector(
    (state) => state.trade.tradeAmountMax?.data
  );
  const coinMarketDetails = useSelector(
    (state) => state.market.singleMarketLoggedInList
  );
  const destinations = useSelector(
    (state) => state.market.marketLoggedInForm?.data?.lisCoinDestination
  );

  const [loading, setLoading] = useState(false);
  const [openModalHelp, setOpenModalHelp] = useState(false);
  const [showMarketDrawer, setShowMarketDrawer] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const borrow = !!searchParams.get("borrow");
  const repay = !!searchParams.get("repay");
  const transfer = !!searchParams.get("transfer");

  const sideId = +searchParams.get("sideId") || 1;
  const walletTypeId = +searchParams.get("walletTypeId") || 1;

  const handleGetInitialStates = async () => {
    setLoading(true);
    dispatch(getMarketLoggedInForm());

    await dispatch(
      getTradeInitial({
        coinId: source,
        coinMasterId: destination,
        sideId,
        walletTypeId: walletTypeId,
      })
    );
    setLoading(false);
  };

  useEffect(() => {
    handleGetInitialStates();

    dispatch(
      getSingleMarketLoggedInList({
        coinSourceId: source,
        coinDestinationId: destination,
      })
    );

    clearInterval(getSingleMarketLoggedInListInterval);

    getSingleMarketLoggedInListInterval = repeat({
      action: () =>
        dispatch(
          getSingleMarketLoggedInList({
            coinSourceId: source,
            coinDestinationId: destination,
          })
        ),
      time: 1000,
    });

    return () => {
      clearInterval(getSingleMarketLoggedInListInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, destination]);
  // ----------------- END TRADE INITIAL ----------------------- //

  useEffect(() => {
    if (tradeInitial?.validSeconds) {
      dispatch(
        getTradeAmountMax({
          coinId: source,
          coinMasterId: destination,
          sideId,
          walletTypeId: walletTypeId,
        })
      );

      clearInterval(getTradeAmountMaxInterval);

      getTradeAmountMaxInterval = repeat({
        action: () =>
          getTradeAmountMax({
            coinId: source,
            coinMasterId: destination,
            sideId,
            walletTypeId: walletTypeId,
          }),
        time: tradeInitial?.validSeconds * 1000,
      });
    }

    return () => {
      clearInterval(getTradeAmountMaxInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tradeInitial?.validSeconds,
    walletTypeId,
    destination,
    source,
    sideId,
    borrow,
    repay,
    transfer,
  ]);

  // ------------------------ END TRADE AMOUNT MAX ----------------------- //

  const allSearchParams = Object.fromEntries([...searchParams]);

  const handleChange = (_, newValue) => {
    if (newValue !== convertTabValue) {
      setSearchParams({ ...allSearchParams, walletTypeId: newValue });
    }
  };

  const handleChangeIndex = (index) => {
    setSearchParams({ ...allSearchParams, walletTypeId: index });
  };

  // ----------------------------- TAB ACTIONS ------------------------ //
  const handleCloseMarketDrawer = () => {
    setShowMarketDrawer(false);
  };

  return (
    <>
      <Container maxWidth="100%">
        <Grid container spacing={1}>
          {!isMobileView && (
            <Grid item xs={12} md={7} lg={8}>
              <TradingViewHeader>
                <TradeHead
                  getSingleMarketLoggedInList={() =>
                    dispatch(
                      getSingleMarketLoggedInList({
                        coinSourceId: source,
                        coinDestinationId: destination,
                      })
                    )
                  }
                  coinMarketDetails={coinMarketDetails}
                  destinations={destinations}
                  setShowMarketDrawer={setShowMarketDrawer}
                />
              </TradingViewHeader>

              <TradingViewChart
                height="432"
                symbol={coinMarketDetails?.coinSourceName}
              />
            </Grid>
          )}

          {!loading ? (
            <Grid item xs={12} md={5} lg={4}>
              <BasicTradeFormWrapper
                getSingleMarketLoggedInList={() =>
                  dispatch(
                    getSingleMarketLoggedInList({
                      coinSourceId: source,
                      coinDestinationId: destination,
                    })
                  )
                }
                setShowMarketDrawer={setShowMarketDrawer}
                coinMarketDetails={coinMarketDetails}
                destinations={destinations}
                tradeInitial={tradeInitial}
                walletTypeId={walletTypeId}
                tradeAmountMax={tradeAmountMax}
                handleChangeIndex={handleChangeIndex}
                handleChange={handleChange}
                setOpenModalHelp={setOpenModalHelp}
              />
            </Grid>
          ) : (
            <Grid item xs={12} md={5} lg={4}>
              <TradeFormSkeleton />
            </Grid>
          )}
        </Grid>

        <Grid container id="trade_history">
          <Grid item xs={12} mb={5}>
            <TradeHistory
              onCancelOrder={() =>
                dispatch(
                  getTradeAmountMax({
                    coinId: source,
                    coinMasterId: destination,
                    sideId,
                    walletTypeId: walletTypeId,
                  })
                )
              }
            />
          </Grid>
        </Grid>

        <MarketDrawer
          onRowClick={(row) => {
            navigate({
              pathname: ROUTE_PATHS.trade.basic(row.data.coinSourceId, row.data.coinDestinationId),
              search: `?${createSearchParams(allSearchParams)}`,
            });

            handleCloseMarketDrawer();
          }}
          open={showMarketDrawer}
          onClose={handleCloseMarketDrawer}
          destinations={destinations}
        />
      </Container>

      {openModalHelp && (
        <Modal
          title={HELP_QUESTIONS_TRADE?.title}
          maxWidth="xs"
          dir="rtl"
          open={openModalHelp}
          onClose={() => setOpenModalHelp(false)}
        >
          <HelpContextWrapper
            dir="rtl"
            dangerouslySetInnerHTML={convertTextToHTML(
              HELP_QUESTIONS_TRADE?.description
            )}
          ></HelpContextWrapper>
        </Modal>
      )}
    </>
  );
}

export const TradingViewHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.alternate,
  borderTopRightRadius: "8px",
  borderTopLeftRadius: "8px",
}));

export const HelpContextWrapper = styled(Box)(({ theme }) => ({
  ".blue-text": {
    color: theme.palette.text.alternate,
  },

  ".red-text": {
    color: theme.palette.error.main,
  },

  "n-weight-500": {
    fontWeight: 500,
  },

  "n-weight-600": {
    fontWeight: 600,
  },

  "n-weight-700": {
    fontWeight: 700,
  },
}));

export default BasicTrade;
